import { Card, CardContent } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Progress } from '@/components/ui/progress';
import { 
  Car, 
  MapPin, 
  Calendar, 
  AlertTriangle, 
  CheckCircle2,
  Clock,
  User,
  Gauge,
  ParkingCircle,
  TrafficCone,
  Phone,
  Zap
} from 'lucide-react';
import { format } from 'date-fns';
import { fr } from 'date-fns/locale';
import { 
  TrafficFine, 
  OffenseType,
  FineStatus,
  OFFENSE_TYPE_LABELS, 
  FINE_STATUS_LABELS,
  getDaysRemaining,
  getCurrentAmount 
} from '@/hooks/useTrafficFines';
import { cn } from '@/lib/utils';

interface FineCardProps {
  fine: TrafficFine;
  onClick?: () => void;
  onDesignate?: () => void;
}

const OFFENSE_ICONS: Record<OffenseType, typeof Car> = {
  speeding: Gauge,
  parking: ParkingCircle,
  red_light: TrafficCone,
  stop_sign: TrafficCone,
  phone_use: Phone,
  seatbelt: User,
  bus_lane: Car,
  pedestrian_crossing: User,
  zfe_violation: Zap,
  toll_evasion: Car,
  fps: ParkingCircle,
  other: AlertTriangle,
};

const STATUS_STYLES: Record<FineStatus, string> = {
  received: 'bg-amber-100 text-amber-800 dark:bg-amber-900/30 dark:text-amber-400',
  pending_designation: 'bg-orange-100 text-orange-800 dark:bg-orange-900/30 dark:text-orange-400',
  designated: 'bg-blue-100 text-blue-800 dark:bg-blue-900/30 dark:text-blue-400',
  contested: 'bg-purple-100 text-purple-800 dark:bg-purple-900/30 dark:text-purple-400',
  paid: 'bg-green-100 text-green-800 dark:bg-green-900/30 dark:text-green-400',
  cancelled: 'bg-muted text-muted-foreground',
};

export function FineCard({ fine, onClick, onDesignate }: FineCardProps) {
  const Icon = OFFENSE_ICONS[fine.offense_type] || AlertTriangle;
  const daysRemaining = getDaysRemaining(fine.designation_deadline);
  const currentAmount = getCurrentAmount(fine);
  const isPending = fine.status === 'received' || fine.status === 'pending_designation';
  
  // Urgency level for designation
  const getUrgencyColor = () => {
    if (!daysRemaining || !isPending) return null;
    if (daysRemaining <= 3) return 'destructive';
    if (daysRemaining <= 7) return 'warning';
    if (daysRemaining <= 15) return 'secondary';
    return null;
  };

  const urgencyColor = getUrgencyColor();

  return (
    <Card 
      className={cn(
        "cursor-pointer hover:shadow-md transition-shadow",
        urgencyColor === 'destructive' && isPending && "border-destructive/50 bg-destructive/5"
      )}
      onClick={onClick}
    >
      <CardContent className="p-4">
        <div className="flex items-start gap-4">
          {/* Icon */}
          <div className={cn(
            "p-2 rounded-lg",
            fine.status === 'paid' 
              ? "bg-green-100 dark:bg-green-900/30" 
              : "bg-muted"
          )}>
            <Icon className={cn(
              "h-5 w-5",
              fine.status === 'paid' 
                ? "text-green-600 dark:text-green-400" 
                : "text-muted-foreground"
            )} />
          </div>

          {/* Content */}
          <div className="flex-1 min-w-0">
            <div className="flex items-start justify-between gap-2">
              <div>
                <h3 className="font-medium text-sm">
                  {OFFENSE_TYPE_LABELS[fine.offense_type]}
                </h3>
                <div className="flex items-center gap-2 mt-1">
                  <Badge variant="outline" className="font-mono text-xs">
                    {fine.license_plate}
                  </Badge>
                  {fine.vehicle && (
                    <span className="text-xs text-muted-foreground">
                      {fine.vehicle.brand} {fine.vehicle.model}
                    </span>
                  )}
                </div>
              </div>
              <Badge className={cn("shrink-0", STATUS_STYLES[fine.status])}>
                {FINE_STATUS_LABELS[fine.status]}
              </Badge>
            </div>

            {/* Details */}
            <div className="mt-3 flex flex-wrap gap-x-4 gap-y-1 text-xs text-muted-foreground">
              {fine.offense_date && (
                <span className="flex items-center gap-1">
                  <Calendar className="h-3 w-3" />
                  {format(new Date(fine.offense_date), 'dd MMM yyyy', { locale: fr })}
                </span>
              )}
              {fine.offense_location && (
                <span className="flex items-center gap-1 truncate max-w-[200px]">
                  <MapPin className="h-3 w-3 shrink-0" />
                  {fine.offense_location}
                </span>
              )}
            </div>

            {/* Speed info if available */}
            {fine.speed_retained && fine.speed_limit && (
              <div className="mt-2 text-xs">
                <span className="text-destructive font-medium">
                  {fine.speed_retained} km/h
                </span>
                <span className="text-muted-foreground"> au lieu de </span>
                <span className="font-medium">{fine.speed_limit} km/h</span>
                {fine.points_withdrawn && fine.points_withdrawn > 0 && (
                  <span className="text-muted-foreground ml-2">
                    (-{fine.points_withdrawn} pts)
                  </span>
                )}
              </div>
            )}

            {/* Driver info */}
            {fine.driver && (
              <div className="mt-2 flex items-center gap-1 text-xs">
                <User className="h-3 w-3 text-muted-foreground" />
                <span>{fine.driver.first_name} {fine.driver.last_name}</span>
                <CheckCircle2 className="h-3 w-3 text-green-500" />
              </div>
            )}

            {/* Designation deadline progress */}
            {isPending && daysRemaining !== null && (
              <div className="mt-3 space-y-1">
                <div className="flex items-center justify-between text-xs">
                  <span className="flex items-center gap-1 text-muted-foreground">
                    <Clock className="h-3 w-3" />
                    Délai désignation
                  </span>
                  <span className={cn(
                    "font-medium",
                    daysRemaining <= 3 && "text-destructive",
                    daysRemaining > 3 && daysRemaining <= 7 && "text-orange-500",
                    daysRemaining > 7 && daysRemaining <= 15 && "text-amber-500"
                  )}>
                    {daysRemaining > 0 ? `J-${daysRemaining}` : daysRemaining === 0 ? "Aujourd'hui" : "Dépassé"}
                  </span>
                </div>
                <Progress 
                  value={Math.max(0, Math.min(100, ((45 - daysRemaining) / 45) * 100))} 
                  className={cn(
                    "h-1.5",
                    daysRemaining <= 3 && "[&>div]:bg-destructive",
                    daysRemaining > 3 && daysRemaining <= 7 && "[&>div]:bg-orange-500",
                    daysRemaining > 7 && daysRemaining <= 15 && "[&>div]:bg-amber-500"
                  )}
                />
              </div>
            )}

            {/* Amount and actions */}
            <div className="mt-3 flex items-center justify-between">
              <div className="text-lg font-semibold">
                {currentAmount.toFixed(2)} €
                {fine.reduced_amount && currentAmount === fine.reduced_amount && (
                  <span className="ml-1 text-xs font-normal text-green-600">
                    (minoré)
                  </span>
                )}
              </div>
              
              {isPending && !fine.driver_id && onDesignate && (
                <Button 
                  size="sm" 
                  variant={urgencyColor === 'destructive' ? 'destructive' : 'outline'}
                  onClick={(e) => {
                    e.stopPropagation();
                    onDesignate();
                  }}
                >
                  <User className="h-4 w-4 mr-1" />
                  Désigner
                </Button>
              )}
            </div>
          </div>
        </div>
      </CardContent>
    </Card>
  );
}
